import axios, { AxiosInstance, AxiosResponse, Canceler } from 'axios';
import { INewAccess } from '../../Components/AccessBlock/Item';
import {
  IOrganizationSettings,
  IStaffSettings,
  ISubdivisionSettings,
} from '../../types/configuration';

export interface ApiAbstractInterface<T, K> {
  list(search: URLSearchParams): Promise<T[] | void>;

  get(id: number): Promise<T>;

  import(): Promise<T>;

  patch(id: number): Promise<T>;

  block(id: number): Promise<T>;

  delete(id: number): Promise<T>;

  unblock(id: number): Promise<T>;

  entry(entity: K): Promise<T>;

  auth(entity: K): Promise<T>;
}

export class ApiAbstract<T, K> implements ApiAbstractInterface<T, K> {
  private http: AxiosInstance;

  private apiPath: string;

  private listCanceller: Canceler | null;

  constructor(http: AxiosInstance, apiPath: string) {
    this.http = http;
    this.apiPath = `/${apiPath}`;
    this.listCanceller = null;
  }

  async list<T>(search = new URLSearchParams('')): Promise<T | void> {
    if (this.listCanceller) {
      this.listCanceller();
    }

    try {
      const response = await this.http.get<T>(`${this.apiPath}`, {
        params: search,
        cancelToken: new axios.CancelToken((c) => {
          this.listCanceller = c;
        }),
      });
      return response.data;
    } catch (e) {
      if (!axios.isCancel(e)) {
        return Promise.reject(e);
      }
      return undefined;
    }
  }

  async get<T>(id: number, search?: URLSearchParams): Promise<T> {
    const response = await this.http.get<T>(
      `${this.apiPath}/${id.toString()}`,
      { params: search },
    );
    return response.data;
  }

  async getTree<T>(id: number, page?: number, limit?: number): Promise<T> {
    let url = `${this.apiPath}/${id.toString()}/tree`;
    const params = new URLSearchParams();
    
    if (page !== undefined) {
      params.append('page', page.toString());
    }
    if (limit !== undefined) {
      params.append('limit', limit.toString());
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await this.http.get<T>(url);
    return response.data;
  }

  async getTreeWithIndeterminate<T>(id: number): Promise<T> {
    const response = await this.http.get<T>(
      `${this.apiPath}/access/${id.toString()}/tree`,
    );
    return response.data;
  }

  async search<T>(str = new URLSearchParams('')): Promise<T> {
    if (this.listCanceller) {
      this.listCanceller();
    }

    const response = await this.http.get<T>(`${this.apiPath}`, {
      params: str,
      // cancelToken: new axios.CancelToken((c) => {
      //   this.listCanceller = c;
      // }),
    });
    return response.data;
  }

  async searchTreeOrg<T>(id: number): Promise<T> {
    const response = await this.http.get<T>(
      `${this.apiPath}/organization/${id.toString()}/tree`,
    );
    return response.data;
  }

  async searchTreeSub<T>(id: number): Promise<T> {
    const response = await this.http.get<T>(
      `${this.apiPath}/subdivision/${id.toString()}/tree`,
    );
    return response.data;
  }

  async searchTreeStaff<T>(id: number): Promise<T> {
    const response = await this.http.get<T>(
      `${this.apiPath}/staff/${id.toString()}/tree`,
    );
    return response.data;
  }

  async entry<T, K>(entity: K): Promise<T> {
    const response = await this.http.post<T>(`${this.apiPath}`, entity);

    return response.data;
  }

  async auth<T, K>(entity: K): Promise<T> {
    const response = await this.http.patch<T>(`${this.apiPath}`, entity);

    return response.data;
  }

  async changeAccessEmployee<T>(
    idEmployee: number,
    newAccess: INewAccess,
  ): Promise<T> {
    const response = await this.http.patch<T>(
      `/analytics/access/${idEmployee}`,
      { ...newAccess },
    );
    return response.data;
  }

  async patch<T>(id: number, type?: number, value?: boolean): Promise<T> {
    const response = type
      ? await this.http.patch<T>(
          `${this.apiPath}/${id.toString()}?type_entity=${type}&value=${value}`,
        )
      : await this.http.patch<T>(`${this.apiPath}/${id.toString()}`);
    return response.data;
  }

  async block<T>(id: number): Promise<T> {
    const response = await this.http.patch<T>(
      `${this.apiPath}/${id.toString()}/block`,
    );
    return response.data;
  }

  async delete<T>(id: number): Promise<T> {
    const response = await this.http.delete<T>(
      `${this.apiPath}/${id.toString()}`,
    );
    return response.data;
  }

  async unblock<T>(id: number): Promise<T> {
    const response = await this.http.patch<T>(
      `${this.apiPath}/${id.toString()}/unblock`,
    );
    return response.data;
  }

  async update<T>(entity: any): Promise<T> {
    const response = await this.http.patch<T>(
      `${this.apiPath}/${entity.id}`,
      entity,
    );
    return response.data;
  }

  async fullUpdate<T>(entity: any): Promise<T> {
    const response = await this.http.put<T>(
      `${this.apiPath}/${entity.employee.id}`,
      entity,
    );
    return response.data;
  }

  async logout<T, K>(token: string): Promise<T> {
    const response = await this.http.get<T>(`${this.apiPath}`, {
      headers: {
        token,
      },
    });

    return response.data;
  }

  async import<T>(): Promise<T> {
    const response = await this.http.get<T>(`${this.apiPath}`);
    return response.data;
  }

  async sendEmail<T>(email: string): Promise<T> {
    const response = await this.http.post<T>(`${this.apiPath}`, null, {
      params: {
        email,
      },
    });
    return response.data;
  }

  async changeParentDivision<T>(
    newParent: number,
    currentId: number,
  ): Promise<T> {
    const response = await this.http.put<T>(
      `/subdivisions/${currentId}`,
      null,
      {
        params: {
          newParent,
        },
      },
    );
    return response.data;
  }

  async getAllSubdivisions<T>(): Promise<T> {
    const response = await this.http.get<T>(`/subdivisions`);
    return response.data;
  }

  async getEmployeeAccess<T>(idEmloyee: number): Promise<T> {
    const response = await this.http.get<T>(`/analytics/access/${idEmloyee}`);
    return response.data;
  }

  async changeStatusStaff<T>(id: number): Promise<T> {
    const response = await this.http.patch<T>(`/staffs/${id}/hide`);
    return response.data;
  }

  async changePasswordAdmin<T>(newPassword: string, id: number): Promise<T> {
    const response = await this.http.patch<T>(
      `/profile/password/send-code/${id}`,
      {
        newPassword,
      },
    );
    return response.data;
  }

  async changePasswordUser<T>(
    oldPassword: string,
    newPassword: string,
  ): Promise<T> {
    const response = await this.http.patch<T>(`/profile/password/send-code`, {
      oldPassword,
      newPassword,
    });
    return response.data;
  }

  async changeAnalyticsAccess<T>(id: number, newAccess: boolean): Promise<T> {
    const response = await this.http.patch<T>(
      `/staffs/analytics/access/${id}?value=${newAccess}`,
    );
    return response.data;
  }

  async getLoginData<T>(id: number): Promise<T> {
    const response = await this.http.get<T>(`/profile/loginData?id=${id}`);
    return response.data;
  }

  async changeParentStaff<T>(
    newSubdivisionID: number,
    currentId: number,
  ): Promise<T> {
    const response = await this.http.patch<T>(`/staffs/${currentId}`, null, {
      params: {
        newSubdivisionID,
      },
    });
    return response.data;
  }

  async restorePassword<T>(entity: T): Promise<T> {
    const response = await this.http.post<T>(`${this.apiPath}`, entity);
    return response.data;
  }

  async getConfigurationStuff<T>(): Promise<T> {
    const response = await this.http.get<T>(`${this.apiPath}/field/staff`);
    return response.data;
  }

  async getConfigurationSubdivision<T>(): Promise<T> {
    const response = await this.http.get<T>(
      `${this.apiPath}/field/subdivision`,
    );
    return response.data;
  }

  async getConfigurationOrganization<T>(): Promise<T> {
    const response = await this.http.get<T>(
      `${this.apiPath}/field/organization`,
    );
    return response.data;
  }

  async changeConfigurationStuff<T>(
    staffSettings: IStaffSettings,
  ): Promise<IStaffSettings> {
    const response = await this.http.post<IStaffSettings>(
      `${this.apiPath}/field/staff`,
      { staffSettings },
    );
    return response.data;
  }

  async changeConfigurationSubdivision<T>(
    subdivisionSettings: ISubdivisionSettings,
  ): Promise<ISubdivisionSettings> {
    const response = await this.http.post<ISubdivisionSettings>(
      `${this.apiPath}/field/subdivision`,
      { subdivisionSettings },
    );
    return response.data;
  }

  async changeConfigurationOrganization<T>(
    organizationSettings: IOrganizationSettings,
  ): Promise<IOrganizationSettings> {
    const response = await this.http.post<IOrganizationSettings>(
      `${this.apiPath}/field/organization`,
      { organizationSettings },
    );
    return response.data;
  }
}
